import { useEffect, useState, useRef, useMemo } from 'react';
import { Box, Textarea, Alert, AlertIcon } from '@chakra-ui/react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMapEvents } from 'react-leaflet';
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_POSITION = [39.547242, 2.729266];

const SelectorDireccionMapa = ({
  alertaMensaje,
  area_de_validez,
  posicionInicial,
  posicion,
  setPosicion,
  markerIconUrl,
  setDireccion  // Nuevo prop para pasar la dirección formateada
}) => {
  // Estado para los geojsons cargados
  const [geojsons, setGeojsons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerta, setAlerta] = useState(false);
  const mapRef = useRef(null);

  // Estado y lógica para el input de dirección
  const {
    value,
    setValue,
    suggestions: { status, data },
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      location: { lat: () => 39.6138, lng: () => 2.9956 },
      radius: 120000,
      componentRestrictions: { country: "es" }
    },
    debounce: 300,
  });

  // Cargar los GeoJSONs del área de validez (rutas)
  useEffect(() => {
    if (!area_de_validez || area_de_validez.length === 0) {
      setGeojsons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let isMounted = true;
    Promise.all(
      area_de_validez.map(ruta =>
        fetch(ruta)
          .then(res => res.json())
          .catch(() => null)
      )
    ).then(data => {
      if (isMounted) setGeojsons(data);
      setLoading(false);
    });
    return () => { isMounted = false; };
  }, [area_de_validez]);

  // Inicializa la posición seleccionada si no existe
  useEffect(() => {
    if (!posicion && posicionInicial) {
      setPosicion(posicionInicial);
    }
    // eslint-disable-next-line
  }, [posicionInicial]);

  // Actualiza el input con la dirección del marker mediante geocoding inverso
  useEffect(() => {
    if (
      posicion &&
      window.google &&
      window.google.maps &&
      window.google.maps.Geocoder
    ) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: posicion[0], lng: posicion[1] } },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const address = results[0].formatted_address;
            setValue(address, false);
            // Actualizar también el estado de dirección en el componente padre
            if (setDireccion) {
              setDireccion(address);
            }
          }
        }
      );
    }
    // eslint-disable-next-line
  }, [posicion]);

  // Función para comprobar si el punto está dentro de algún polígono
  function pointInPolygon(point, geojsons) {
    if (!geojsons || geojsons.length === 0) return false;
    const pt = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [point[1], point[0]], // GeoJSON usa [lng, lat]
      },
    };
    for (const geojson of geojsons) {
      if (!geojson) continue;
      if (geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) {
        for (const feature of geojson.features) {
          if (booleanPointInPolygon(pt, feature)) {
            return true;
          }
        }
      } else if (
        geojson.type === "Feature" &&
        (geojson.geometry.type === "Polygon" || geojson.geometry.type === "MultiPolygon")
      ) {
        if (booleanPointInPolygon(pt, geojson)) {
          return true;
        }
      }
    }
    return false;
  }

  // Handler para seleccionar dirección del input
  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      const selectedPoint = [lat, lng];

      if (pointInPolygon(selectedPoint, geojsons)) {
        setAlerta(false);
        setPosicion(selectedPoint);
        console.log('[SelectorDireccionMapa] Llamando setDireccion con:', results[0].formatted_address);
        if (setDireccion) setDireccion(results[0].formatted_address);
        setTimeout(() => {
          if (mapRef.current && mapRef.current.setView) {
            mapRef.current.setView(selectedPoint, mapRef.current.getZoom(), { animate: true });
          }
        }, 100);
      } else {
        setAlerta(true);
      }
    } catch (error) {
      setAlerta(true);
    }
  };

  // Icono personalizado para el marcador, solo si la URL no es vacía
  const icon = useMemo(() => {
    if (markerIconUrl && markerIconUrl.trim() !== '') {
      return L.icon({
        iconUrl: markerIconUrl,
        iconSize: [32, 48],
        iconAnchor: [16, 48],
        popupAnchor: [0, -48],
      });
    }
    return undefined; // Usar el icono por defecto de Leaflet
  }, [markerIconUrl]);

  function LocationMarker() {
    useMapEvents({
      click(e) {
        const latlng = [e.latlng.lat, e.latlng.lng];
        if (!pointInPolygon(latlng, geojsons)) {
          setAlerta(true);
        } else {
          setAlerta(false);
          setPosicion(latlng);

          // Consulta inversa para obtener la dirección y actualizar el input
          if (window.google && window.google.maps && window.google.maps.Geocoder) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: latlng[0], lng: latlng[1] } }, (results, status) => {
              if (status === 'OK' && results[0]) {
                setValue(results[0].formatted_address, false);
                console.log('[SelectorDireccionMapa] Click en mapa - Llamando setDireccion con:', results[0].formatted_address);
                if (setDireccion) setDireccion(results[0].formatted_address);
              }
            });
          }
        }
      },
    });

    if (!posicion) return null;
    // Solo pasa el prop icon si existe, si no, deja que Marker use el icono por defecto
    return icon
      ? <Marker position={posicion} icon={icon} />
      : <Marker position={posicion} />;
  }

  return (
    <Box w="100%" maxW="500px">
      {/* Input de dirección */}
      <Box position="relative">
        <Textarea
          placeholder="Introduce una dirección"
          value={value}
          onChange={e => setValue(e.target.value)}
          minHeight="60px"
          resize="vertical"
          rows={2}
        />
        {status === "OK" && data.length > 0 && (
          <Box
            border="1px solid #e2e8f0"
            borderRadius="md"
            bg="black"
            mt={1}
            maxH="200px"
            overflowY="auto"
            zIndex={10000}
            position="absolute"
            width="100%"
          >
            {data.map(s => (
              <Box
                key={s.place_id}
                px={3}
                py={2}
                color="white"
                _hover={{ bg: "gray.700", cursor: "pointer" }}
                onClick={() => handleSelect(s.description)}
              >
                {s.description}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Mensaje de alerta */}
      {alerta && (
        <Alert status="error" maxW="500px" mt={2}>
          <AlertIcon />
          <Box color="black">{alertaMensaje}</Box>
        </Alert>
      )}

      {/* Mapa */}
      <Box w="100%" h="350px" mt={4}>
        {!loading && (
          <MapContainer
            center={posicion || posicionInicial || DEFAULT_POSITION}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            whenCreated={mapInstance => { mapRef.current = mapInstance; }}
            key={posicion ? posicion.join(',') : 'default'}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geojsons && geojsons.map(
              (geojson, idx) =>
                geojson && (
                  <GeoJSON
                    key={idx}
                    data={geojson}
                    style={{
                      color: idx === 0 ? "#3182ce" : "#e67e22",
                      weight: 2,
                      fillOpacity: 0.1,
                    }}
                  />
                )
            )}
            <LocationMarker />
          </MapContainer>
        )}
      </Box>
    </Box>
  );
};

export default SelectorDireccionMapa;